/**
 * @fileoverview Hook for fetching grade options
 * @description Reusable hook to fetch and cache grade options
 */

import { useState, useEffect } from 'react'
import { getGrades } from '../service'
import type { Grade } from '../service/types'

/**
 * Hook to fetch grade options
 * @returns grades list, loading state, and error state
 */
export const useGrades = (fallbackErrorMessage = 'Failed to load grades') => {
  const [grades, setGrades] = useState<Grade[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadGrades = async () => {
      setLoading(true)
      setError(null)
      try {
        const result = await getGrades()
        setGrades(result)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : fallbackErrorMessage
        setError(errorMessage)
      } finally {
        setLoading(false)
      }
    }
    loadGrades()
  }, [])

  const gradeOptions = grades.map((grade) => ({
    value: grade.value,
    label: grade.label,
  }))

  return { grades, gradeOptions, loading, error }
}
